import { useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import EmailIcon from '../../../assets/ci/profile/email.svg';
import PhoneIcon from '../../../assets/ci/profile/phone.svg';
import { useThemeComponents } from '../../../themes';
import breakpoints from '../../../utilities/breakpoints';
import { SecondaryText } from '../UserSelfDrawer/styled';

export const Container = styled.div`
    display: flex;
    align-items: center;

    & .p-button {
        line-height: normal;

        @media screen and (max-width: 345px) {
            height: 100%;
            margin-right: -1.5rem;
        }
    }

    & .psme-select-language {
        display: flex;
        align-items: center;

        @media screen and (min-width: ${breakpoints.md}) {
            margin: 0px;
        }
    }

    & .psme-select-language > div {
        padding: 0px;
    }

    & .header-info-item span {
        font-size: 1rem;
        color: #262626;
    }

    & .header-info-item:not(.psme-select-language)::before {
        display: none;
    }

    & .header-info-item:not(.psme-select-language)::after {
        display: none;
    }

    & .header-info-item:not(:last-of-type)::after {
        display: block;
        width: 1px;
        height: 1.25rem;
        margin: 0 1rem;
        background-color: #e3e4e5;
        content: '';
    }

    & .header-info-item:last-of-type:not(.psme-select-language) {
        margin-right: 1.25rem;
    }

    & .header-info-item.psme-select-language:not(:nth-last-child(1)) {
        margin-right: 1rem;
    }

    @media screen and (max-width: 999px) {
        .book-button {
            flex-direction: column;
        }
        & .ant-btn {
            height: 100%;
            margin-right: -1.5rem;
        }
        & .header-info-item:not(.psme-select-language)::before {
            display: block;
            width: 1px;
            height: 100%;
            background-color: #e3e4e5;
            content: '';
        }
        & .header-info-item:not(.psme-select-language)::after {
            display: block;
            width: 1px;
            height: 100%;
            background-color: #e3e4e5;
            content: '';
        }
        & .header-info-item:not(.psme-select-language) {
            svg {
                margin: 4.2px 1rem;
            }
        }
        & .header-info-item:last-of-type:not(.psme-select-language) {
            margin-right: 0;
            svg {
                margin: 4.2px 2.5rem;
            }
        }
        & .header-info-item:last-child:not(.psme-select-language) {
            position: relative;
            left: 24px;
        }
        & .header-info-item:not(:last-of-type)::after {
            height: 100%;
            margin: 0;
        }
        & .header-info-item.psme-select-language:not(:nth-last-child(1)) {
            margin-right: 1.25rem;
        }
        & :last-of-type:is(.psme-select-language) {
            position: relative;
            left: 0.75rem;
        }
        & :last-child:is(.psme-select-language) {
            left: 1.25rem;
            margin-right: 0.5rem;
        }
    }
`;

const StyledLink = styled(Link).attrs(() => ({
    color: '#000000',
}))`
    color: #000000;
    text-decoration-color: #000000;
    display: flex;
    align-items: center;
`;

const ContactItemWrapper = styled.div<{ hasTitle: boolean }>`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;

    svg {
        margin: 4.2px ${props => (props.hasTitle ? '0.6rem' : '2px')};
        fill-opacity: 0.45;
    }
`;

type ContactItemProps = {
    icon: React.ReactNode;
    title?: React.ReactNode;
    href?: string;
    className?: string;
    hasMaxWidth?: boolean;
};

const ContactItem = ({ icon, title, href, className, hasMaxWidth = true }: ContactItemProps) => {
    const content = useMemo(
        () => (
            <>
                {icon}
                {title && <SecondaryText hasMaxWidth={hasMaxWidth}>{title}</SecondaryText>}
            </>
        ),
        [icon, title, hasMaxWidth]
    );

    return (
        <ContactItemWrapper className={className} hasTitle={!!title}>
            {href ? <StyledLink to={href}>{content}</StyledLink> : content}
        </ContactItemWrapper>
    );
};

export const Phone = ({ phoneNumber, showTitle }: { phoneNumber: string; showTitle: boolean }) => (
    <ContactItem
        className="header-info-item"
        hasMaxWidth={false}
        href={`tel:${phoneNumber}`}
        icon={<PhoneIcon />}
        title={showTitle ? phoneNumber : null}
    />
);

export const Email = ({ email }: { email: string }) => (
    <ContactItem className="header-info-item" hasMaxWidth={false} href={`mailto:${email}`} icon={<EmailIcon />} />
);

export const BookButton = ({ title, mobilityApplicationUrl }: { title: string; mobilityApplicationUrl: string }) => {
    const { Button } = useThemeComponents();

    const onBookNowClicked = useCallback(() => window.open(mobilityApplicationUrl, '_self'), [mobilityApplicationUrl]);

    if (!mobilityApplicationUrl) {
        return null;
    }

    return (
        <Button className="book-button" onClick={onBookNowClicked} type="primary">
            {title ?? ''}
        </Button>
    );
};
